import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../sidebar/Sidebar.js";
import api from "../api/api";
import { useSearchInvalidation } from "../hooks/useSearchInvalidation.js";

export default function ReceiptCapture() {
  const navigate = useNavigate();
  const { invalidateCategory } = useSearchInvalidation();
  const [location, setLocation] = useState("Receipt Capture");
  const [step, setStep] = useState("capture"); // 'capture', 'preview', 'naming', 'uploading'
  const [capturedImage, setCapturedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [receiptName, setReceiptName] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(
      navigator.userAgent
    );

  // Handle file selection from camera
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCapturedImage(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);

      setStep("preview");
      setError("");
    }
  };

  // Trigger camera capture
  const openCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    setStep("capture");
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Proceed to naming
  const proceedToNaming = () => {
    setStep("naming");
  };

  // Upload receipt
  const uploadReceipt = async () => {
    if (!capturedImage || !receiptName.trim()) {
      setError("Please provide a name for the receipt");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", capturedImage);
      formData.append("original_name", receiptName.trim());

      const response = await api.post("/receipts/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        // Invalidate receipts cache to update global search index
        invalidateCategory("receipts");
        // If success - navigate to processing page with receipt data
        navigate("/Receipt-Processing", {
          state: { receipt: response.data },
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.detail || "Failed to upload receipt");
    } finally {
      setUploading(false);
    }
  };

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const renderCaptureStep = () => (
    <div className="flex flex-col h-full">
      {/* Camera Capture Interface */}
      <div className="flex-1 bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Capture Receipt
          </h2>
          <p className="text-gray-600 mb-6">
            Take a photo of your receipt using your device camera
          </p>

          {/* Camera Icon */}
          <div className="w-24 h-24 mx-auto mb-6 bg-blue-500 rounded-full flex items-center justify-center">
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>

          {/* Hidden file input for camera capture */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Capture Button */}
          {isMobile ? (
            <button
              onClick={openCamera}
              className="w-full max-w-sm px-6 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
            >
              Open Camera
            </button>
          ) : (
            <p className="text-red-600 font-medium">
              Please use a mobile device to capture receipts.
            </p>
          )}
        </div>

        {error && (
          <div className="max-w-sm w-full p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="flex flex-col h-full">
      {/* Image Preview */}
      <div className="flex-1 bg-gray-50 flex items-center justify-center p-4">
        {imagePreviewUrl && (
          <img
            src={imagePreviewUrl}
            alt="Captured receipt"
            className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
          />
        )}
      </div>

      {/* Controls */}
      <div className="bg-white p-6 flex justify-between items-center border-t">
        <button
          onClick={retakePhoto}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Retake
        </button>

        <button
          onClick={proceedToNaming}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Use Photo
        </button>
      </div>
    </div>
  );

  const renderNamingStep = () => (
    <div className="flex flex-col h-full">
      {/* Form */}
      <div className="flex-1 p-6 bg-gray-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Name Your Receipt
          </h2>

          {/* Thumbnail */}
          {imagePreviewUrl && (
            <div className="mb-6 flex justify-center">
              <img
                src={imagePreviewUrl}
                alt="Receipt thumbnail"
                className="w-32 h-24 object-cover rounded-lg shadow-md"
              />
            </div>
          )}

          {/* Name Input */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Receipt Name
            </label>
            <input
              type="text"
              value={receiptName}
              onChange={(e) => setReceiptName(e.target.value)}
              placeholder="e.g., Office Supplies - Home Depot"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={100}
            />
            <p className="text-gray-500 text-xs mt-1">
              {receiptName.length}/100 characters
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="p-6 flex justify-between items-center border-t">
        <button
          onClick={() => setStep("preview")}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Back
        </button>

        <button
          onClick={uploadReceipt}
          disabled={uploading || !receiptName.trim()}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {uploading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          )}
          <span>{uploading ? "Uploading..." : "Save Receipt"}</span>
        </button>
      </div>
    </div>
  );

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="min-h-full flex flex-col bg-gray-50 py-4 px-6">
        {step === "capture" && renderCaptureStep()}
        {step === "preview" && renderPreviewStep()}
        {step === "naming" && renderNamingStep()}
      </div>
    </MainLayout>
  );
}
